import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  FileText,
  Save,
  Loader2,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  Star,
  XCircle,
  Image,
  Palette,
  MapPin,
  FileCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientIntakeForm({ leadId, onUpdate, embedded = false }) {
  const [intakeData, setIntakeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [buildReadiness, setBuildReadiness] = useState(null);

  useEffect(() => {
    fetchIntake();
    fetchBuildReadiness();
  }, [leadId]);

  const fetchIntake = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/leads/${leadId}/intake`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setIntakeData(data);
      }
    } catch (error) {
      console.error("Failed to fetch intake data:", error);
      toast.error("Failed to load intake data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildReadiness = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}/build-readiness`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setBuildReadiness(data);
      }
    } catch (error) {
      console.error("Failed to fetch build readiness:", error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/leads/${leadId}/intake`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          legal_name: intakeData.legal_name,
          dba_name: intakeData.dba_name,
          intake_phone: intakeData.intake_phone,
          intake_email: intakeData.intake_email,
          intake_address: intakeData.intake_address,
          service_area_business: intakeData.service_area_business,
          intake_city_list: intakeData.intake_city_list,
          intake_county_list: intakeData.intake_county_list,
          license_type: intakeData.license_type,
          license_number: intakeData.license_number,
          license_url: intakeData.license_url,
          business_hours: intakeData.business_hours,
          price_range: intakeData.price_range,
          payments_accepted: intakeData.payments_accepted,
          client_reviews: intakeData.client_reviews
        })
      });

      if (response.ok) {
        toast.success("Intake data saved");
        fetchIntake();
        fetchBuildReadiness();
        if (onUpdate) onUpdate();
      } else {
        toast.error("Failed to save intake data");
      }
    } catch (error) {
      console.error("Failed to save intake:", error);
      toast.error("Failed to save intake data");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setIntakeData(prev => ({ ...prev, [field]: value }));
  };

  const updateAddress = (field, value) => {
    setIntakeData(prev => ({
      ...prev,
      intake_address: { ...prev.intake_address, [field]: value }
    }));
  };

  const addReview = () => {
    setIntakeData(prev => ({
      ...prev,
      client_reviews: [
        ...(prev.client_reviews || []),
        { author: "", rating: 5, text: "", date: new Date().toISOString().split("T")[0] }
      ]
    }));
  };

  const updateReview = (index, field, value) => {
    setIntakeData(prev => {
      const reviews = [...(prev.client_reviews || [])];
      reviews[index] = { ...reviews[index], [field]: value };
      return { ...prev, client_reviews: reviews };
    });
  };

  const removeReview = (index) => {
    setIntakeData(prev => ({
      ...prev,
      client_reviews: prev.client_reviews.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      );
    }
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!intakeData) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center py-8">
          <p className="text-muted-foreground">Failed to load intake data</p>
        </div>
      );
    }
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Failed to load intake data</p>
        </CardContent>
      </Card>
    );
  }

  const isReadyForBuild = intakeData.ready_for_build;

  const formContent = (
    <div className="space-y-6">
      {/* Status badge for embedded mode */}
      {embedded && (
        <div className="flex items-center justify-between pb-2 border-b">
          <span className="text-sm text-muted-foreground">Build Status</span>
          {isReadyForBuild ? (
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
              <CheckCircle size={14} className="mr-1" />
              Ready for Build
            </Badge>
          ) : (
            <Badge variant="secondary">
              <AlertCircle size={14} className="mr-1" />
              Incomplete
            </Badge>
          )}
        </div>
      )}
      
      {/* Build Readiness Checklist */}
      {buildReadiness && (
        <div className="p-4 bg-muted/50 rounded-lg">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <FileCheck size={16} />
            Build Readiness Checklist
          </h4>
          <div className="grid grid-cols-2 gap-2">
                {Object.entries(buildReadiness.checklist).map(([key, item]) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    {item.status ? (
                      <CheckCircle size={14} className="text-green-600" />
                    ) : (
                      <XCircle size={14} className="text-muted-foreground" />
                    )}
                    <span className={item.status ? "text-foreground" : "text-muted-foreground"}>
                      {item.label}
                      {item.required && !item.status && (
                        <span className="text-destructive ml-1">*</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              {buildReadiness.ready_for_build ? (
                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                  <CheckCircle size={12} />
                  All required items complete - ready to build full site
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-2">
                  Complete required items (*) to enable full site build
                </p>
              )}
            </div>
          )}

          {/* Business Identity */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Business Identity
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Legal Business Name</Label>
                <Input
                  value={intakeData.legal_name || ""}
                  onChange={(e) => updateField("legal_name", e.target.value)}
                  placeholder="ABC Services LLC"
                />
              </div>
              <div className="form-group">
                <Label>DBA / Trade Name</Label>
                <Input
                  value={intakeData.dba_name || ""}
                  onChange={(e) => updateField("dba_name", e.target.value)}
                  placeholder="ABC Services"
                />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Contact Information
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Phone (for Schema)</Label>
                <Input
                  value={intakeData.intake_phone || ""}
                  onChange={(e) => updateField("intake_phone", e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="form-group">
                <Label>Email (for Schema)</Label>
                <Input
                  type="email"
                  value={intakeData.intake_email || ""}
                  onChange={(e) => updateField("intake_email", e.target.value)}
                  placeholder="contact@business.com"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Business Address
            </h4>
            <div className="space-y-3">
              <div className="form-group">
                <Label>Street Address</Label>
                <Input
                  value={intakeData.intake_address?.street || ""}
                  onChange={(e) => updateAddress("street", e.target.value)}
                  placeholder="123 Main St"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="form-group">
                  <Label>City</Label>
                  <Input
                    value={intakeData.intake_address?.city || ""}
                    onChange={(e) => updateAddress("city", e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <Label>State</Label>
                  <Input
                    value={intakeData.intake_address?.state || ""}
                    onChange={(e) => updateAddress("state", e.target.value)}
                    placeholder="TX"
                  />
                </div>
                <div className="form-group">
                  <Label>ZIP</Label>
                  <Input
                    value={intakeData.intake_address?.zip || ""}
                    onChange={(e) => updateAddress("zip", e.target.value)}
                    placeholder="78701"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Service Area */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Service Area
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Switch
                  checked={intakeData.service_area_business}
                  onCheckedChange={(checked) => updateField("service_area_business", checked)}
                />
                <Label className="font-normal">Service Area Business (travels to customers)</Label>
              </div>
              <div className="form-group">
                <Label>Cities Served (comma-separated)</Label>
                <Input
                  value={(intakeData.intake_city_list || []).join(", ")}
                  onChange={(e) => updateField(
                    "intake_city_list",
                    e.target.value.split(",").map(c => c.trim()).filter(Boolean)
                  )}
                  placeholder="Austin, Round Rock, Cedar Park"
                />
              </div>
              <div className="form-group">
                <Label>Counties Served (comma-separated)</Label>
                <Input
                  value={(intakeData.intake_county_list || []).join(", ")}
                  onChange={(e) => updateField(
                    "intake_county_list",
                    e.target.value.split(",").map(c => c.trim()).filter(Boolean)
                  )}
                  placeholder="Travis County, Williamson County"
                />
              </div>
            </div>
          </div>

          {/* License */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              License Information (Optional)
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="form-group">
                <Label>License Type</Label>
                <Input
                  value={intakeData.license_type || ""}
                  onChange={(e) => updateField("license_type", e.target.value)}
                  placeholder="Contractor License"
                />
              </div>
              <div className="form-group">
                <Label>License Number</Label>
                <Input
                  value={intakeData.license_number || ""}
                  onChange={(e) => updateField("license_number", e.target.value)}
                  placeholder="#12345"
                />
              </div>
              <div className="form-group">
                <Label>Verification URL</Label>
                <Input
                  value={intakeData.license_url || ""}
                  onChange={(e) => updateField("license_url", e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
              Pricing & Payments
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Price Range</Label>
                <Select
                  value={intakeData.price_range || "$$"}
                  onValueChange={(val) => updateField("price_range", val)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="$">$ - Budget Friendly</SelectItem>
                    <SelectItem value="$$">$$ - Moderate</SelectItem>
                    <SelectItem value="$$$">$$$ - Premium</SelectItem>
                    <SelectItem value="$$$$">$$$$ - Luxury</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label>Payment Methods (comma-separated)</Label>
                <Input
                  value={(intakeData.payments_accepted || []).join(", ")}
                  onChange={(e) => updateField(
                    "payments_accepted",
                    e.target.value.split(",").map(p => p.trim()).filter(Boolean)
                  )}
                  placeholder="Cash, Credit Card, Check"
                />
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <Collapsible open={hoursOpen} onOpenChange={setHoursOpen}>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground">
              Business Hours (Optional)
              <span className="text-xs font-normal normal-case">
                {hoursOpen ? "▼" : "►"}
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="grid grid-cols-2 gap-2">
                {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((day) => (
                  <div key={day} className="flex items-center gap-2">
                    <Label className="w-10 text-xs uppercase">{day}</Label>
                    <Input
                      value={intakeData.business_hours?.[day] || ""}
                      onChange={(e) => updateField("business_hours", {
                        ...intakeData.business_hours,
                        [day]: e.target.value
                      })}
                      placeholder="8am-6pm"
                      className="text-sm"
                    />
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Client Reviews */}
          <Collapsible open={reviewsOpen} onOpenChange={setReviewsOpen}>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground">
              Client Reviews ({(intakeData.client_reviews || []).length})
              <span className="text-xs font-normal normal-case">
                {reviewsOpen ? "▼" : "►"}
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Only add real reviews provided by the client. Never invent reviews.
                </p>
                {(intakeData.client_reviews || []).map((review, idx) => (
                  <div key={idx} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Input
                          value={review.author}
                          onChange={(e) => updateReview(idx, "author", e.target.value)}
                          placeholder="Reviewer name"
                          className="w-40"
                        />
                        <Select
                          value={String(review.rating)}
                          onValueChange={(val) => updateReview(idx, "rating", parseInt(val))}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[5, 4, 3, 2, 1].map(r => (
                              <SelectItem key={r} value={String(r)}>
                                {r} <Star size={12} className="inline ml-1" />
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeReview(idx)}
                        className="text-destructive"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                    <Textarea
                      value={review.text}
                      onChange={(e) => updateReview(idx, "text", e.target.value)}
                      placeholder="Review text..."
                      className="text-sm"
                    />
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addReview}>
                  <Plus size={14} className="mr-1" />
                  Add Review
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Save Button */}
          <div className="pt-4 border-t">
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Intake Data
                </>
              )}
            </Button>
          </div>
        </div>
  );

  // If embedded, return just the content
  if (embedded) {
    return formContent;
  }

  // Otherwise return full Card
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <FileText size={20} className="text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Client Intake (Admin Only)</CardTitle>
              <CardDescription>Schema data for full site generation</CardDescription>
            </div>
          </div>
          {isReadyForBuild ? (
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
              <CheckCircle size={14} className="mr-1" />
              Ready for Build
            </Badge>
          ) : (
            <Badge variant="secondary">
              <AlertCircle size={14} className="mr-1" />
              Incomplete
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {formContent}
      </CardContent>
    </Card>
  );
}
